/**
 * One socket for the whole tab.
 *
 * Components never call `socket.on`. There is exactly one listener, installed
 * by `startRealtime`, because the sequence check that detects dropped packets
 * can only work if every packet passes through the same place — and a second
 * listener installed by a component that later unmounts is how a board goes
 * quietly stale.
 */

import { io, type Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@shared/socket';

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export const socket: AppSocket = io({
  path: '/socket.io',
  // Same origin, so the Better Auth session cookie authenticates the handshake.
  withCredentials: true,
  // Nothing connects until `/api/me` has answered. A socket that connects
  // before the session is known just gets refused at the handshake and starts
  // a pointless reconnect loop behind the login screen.
  autoConnect: false,
  reconnectionDelay: 500,
  reconnectionDelayMax: 5000,
});

/* -------------------------------------------------------------------------- */
/* Connection status, as an external store the UI can subscribe to             */
/* -------------------------------------------------------------------------- */

export type ConnectionState = 'idle' | 'connecting' | 'live' | 'offline';

let connection: ConnectionState = 'idle';
const listeners = new Set<() => void>();

export function setConnection(next: ConnectionState): void {
  if (connection === next) return;
  connection = next;
  for (const listener of listeners) listener();
}

export function getConnection(): ConnectionState {
  return connection;
}

export function subscribeConnection(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Join the room that carries one thread's messages. */
export function watchThread(conversationId: string): void {
  socket.emit('thread:watch', conversationId);
}

export function unwatchThread(conversationId: string): void {
  socket.emit('thread:unwatch', conversationId);
}
